import { motion, AnimatePresence } from "framer-motion";
import { X, Bell, Zap, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { backendFetch, invalidateBackendCache } from "@/lib/backend";
import { useOnboarding } from "@/context/OnboardingContext";
import { showError } from "@/utils/toast";
import { useGsapActionMagnet, useGsapNotificationStack } from "@/hooks/useOrizonteMotion";
import { isSafeInternalRoute } from "@/lib/safe-actions";

type NotificationItem = {
  id: string;
  title: string;
  message: string;
  time: string;
  type: "warning" | "danger" | "action" | "success";
  icon: string;
  actionPath: string;
  read: boolean;
};

export const NotificationPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [pendingReadIds, setPendingReadIds] = useState<string[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useUser();
  const { profile, loading: profileLoading } = useOnboarding();

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    document.addEventListener("open-notifications", handleOpen);
    return () => document.removeEventListener("open-notifications", handleOpen);
  }, []);

  useEffect(() => {
    if (!user || profileLoading || !profile?.onboardingCompleted) {
      setNotifications([]);
      return;
    }

    let cancelled = false;
    const syncNotifications = async (forceRefresh = false) => {
      try {
        const data = await backendFetch('/notifications', {
          forceRefresh,
          staleTtlMs: 15_000,
        });
        if (!cancelled) {
          const notes = (Array.isArray(data.notifications) ? data.notifications : []) as Record<string, unknown>[];
          setNotifications(notes.map((note) => ({
            id: String(note.id),
            title: String(note.title || ''),
            message: String(note.message || ''),
            time: String(note.time || 'Just now'),
            type: (note.type as NotificationItem['type']) || 'action',
            icon: String(note.icon || 'Zap'),
            actionPath: String(note.actionPath || '/dashboard'),
            read: Boolean(note.read || false),
          })));
        }
      } catch {
        if (!cancelled) setNotifications([]);
      }
    };

    syncNotifications();
    const interval = window.setInterval(() => syncNotifications(true), 20000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [profileLoading, profile?.onboardingCompleted, user]);

  const announceNotificationsChanged = (unreadCount?: number) => {
    invalidateBackendCache("/notifications");
    document.dispatchEvent(new CustomEvent("notifications-changed", {
      detail: typeof unreadCount === "number" ? { unreadCount } : undefined,
    }));
  };

  const handleActionClick = async (path: string, id: string) => {
    if (!isSafeInternalRoute(path)) {
      showError("Blocked an unsafe notification target");
      return;
    }

    if (pendingReadIds.includes(id)) return;

    if (user) {
      const nextUnreadCount = notifications.reduce((count, note) => {
        if (note.id === id) return count;
        return count + (note.read ? 0 : 1);
      }, 0);
      setNotifications((prev) => prev.map((note) => note.id === id ? { ...note, read: true } : note));
      setPendingReadIds((prev) => [...prev, id]);
      announceNotificationsChanged(nextUnreadCount);
    }

    setIsOpen(false);
    navigate(path);

    if (!user) return;

    try {
      await backendFetch(`/notifications/${id}/read`, { method: 'PATCH' });
    } catch (error) {
      console.error(error);
    } finally {
      setPendingReadIds((prev) => prev.filter((pendingId) => pendingId !== id));
      announceNotificationsChanged();
    }
  };

  const handleMarkAllRead = async () => {
    if (!user || notifications.length === 0) {
      setIsOpen(false);
      return;
    }

    setIsMarkingRead(true);
    setNotifications((prev) => prev.map((note) => ({ ...note, read: true })));
    announceNotificationsChanged(0);

    try {
      await backendFetch('/notifications/read-all', { method: 'PATCH' });
      announceNotificationsChanged();
    } catch (error) {
      console.error(error);
      showError("Could not mark alerts as read. Please try again.");
    } finally {
      setIsMarkingRead(false);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  useGsapNotificationStack(panelRef, [isOpen, notifications.length, unreadCount]);
  useGsapActionMagnet(panelRef, "[data-gsap-magnet]", [isOpen, notifications.length, unreadCount]);

  const iconMap: Record<string, React.ElementType> = {
    TrendingUp,
    Zap,
    AlertTriangle,
    CheckCircle2,
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
          />
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[110] w-full md:w-[400px] bg-orizonte-surface border-l border-white/[0.05] shadow-2xl flex flex-col"
          >
            <div className="h-14 sm:h-16 border-b border-white/[0.05] flex items-center justify-between px-4 sm:px-6 bg-white/[0.01] shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-white/[0.05] rounded-md border border-white/10 text-orizonte-textMuted">
                  <Bell className="w-4 h-4" />
                </div>
                <h2 className="text-sm font-semibold text-orizonte-text">Telemetry Alerts</h2>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  data-gsap-magnet
                  className="text-xs text-orizonte-lime hover:text-orizonte-emerald transition-colors font-medium px-2"
                  onClick={handleMarkAllRead}
                  disabled={isMarkingRead || unreadCount === 0}
                >
                  {isMarkingRead ? "Marking..." : unreadCount > 0 ? `Mark ${unreadCount} read` : "All read"}
                </button>
                <button 
                  onClick={() => setIsOpen(false)}
                  data-gsap-magnet
                  className="p-1.5 text-orizonte-textMuted hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 sm:space-y-3">
              {notifications.length === 0 && (
                <div className="py-12 text-center text-sm text-orizonte-textMuted">
                  No alerts yet. Intelligence engine will notify you here.
                </div>
              )}
              {notifications.map((note) => {
                const typeStyles = {
                  warning: "text-yellow-500 bg-yellow-500/10 border-yellow-500/20",
                  danger: "text-red-400 bg-red-500/10 border-red-500/20",
                  action: "text-orizonte-lime bg-orizonte-lime/10 border-orizonte-lime/20",
                  success: "text-orizonte-emerald bg-orizonte-emerald/10 border-orizonte-emerald/20"
                }[note.type];

                const Icon = iconMap[note.icon] || Zap;

                return (
                  <div 
                    key={note.id}
                    role="button"
                    tabIndex={0}
                    data-notification-item
                    data-notification-unread={!note.read ? true : undefined}
                    onClick={() => handleActionClick(note.actionPath, note.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleActionClick(note.actionPath, note.id);
                      }
                    }}
                    className={`p-3 sm:p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] border border-white/5 transition-colors cursor-pointer group flex gap-3 sm:gap-4 ${
                      !note.read ? "border-l-2 border-l-orizonte-lime" : ""
                    }`}
                  >
                    <div className={`p-2 rounded-lg shrink-0 h-fit ${typeStyles}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h4 className="text-sm font-medium text-orizonte-text group-hover:text-orizonte-lime transition-colors">{note.title}</h4>
                      </div>
                      <p className="text-xs text-orizonte-textMuted leading-relaxed mb-2">
                        {note.message}
                      </p>
                      <span className="text-[10px] text-orizonte-textMuted uppercase tracking-wider font-medium">
                        {note.time}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
